import { usersAPI } from '@/api'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import type {
  CrmIntegrationOutgoingAttachment,
  CrmIntegrationOutgoingAttachmentKind,
} from '../types/crm-integration-chat.types'

export const CRM_INTEGRATION_OUTGOING_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
] as const

export const CRM_INTEGRATION_OUTGOING_IMAGE_ACCEPT =
  CRM_INTEGRATION_OUTGOING_IMAGE_EXTENSIONS.map((ext) => `.${ext}`).join(',')

export const CRM_INTEGRATION_OUTGOING_DOCUMENT_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
  'txt',
  'rtf',
  'csv',
  'zip',
  'rar',
  '7z',
  'tar',
  'gz',
] as const

export const CRM_INTEGRATION_OUTGOING_VIDEO_EXTENSIONS = [
  'mp4',
  'mov',
  'mkv',
  'webm',
  'avi',
] as const

export const CRM_INTEGRATION_OUTGOING_FILE_ACCEPT = [
  ...CRM_INTEGRATION_OUTGOING_IMAGE_EXTENSIONS,
  ...CRM_INTEGRATION_OUTGOING_DOCUMENT_EXTENSIONS,
  ...CRM_INTEGRATION_OUTGOING_VIDEO_EXTENSIONS,
]
  .map((ext) => `.${ext}`)
  .join(',')

const IMAGE_EXTENSION_SET = new Set<string>(
  CRM_INTEGRATION_OUTGOING_IMAGE_EXTENSIONS,
)

const DOCUMENT_EXTENSION_SET = new Set<string>(
  CRM_INTEGRATION_OUTGOING_DOCUMENT_EXTENSIONS,
)

const VIDEO_EXTENSION_SET = new Set<string>(
  CRM_INTEGRATION_OUTGOING_VIDEO_EXTENSIONS,
)

const TELEGRAM_VIDEO_MAX_BYTES = 50 * 1024 * 1024
const VK_VIDEO_MAX_BYTES = 200 * 1024 * 1024
const MAX_VIDEO_MAX_BYTES = 250 * 1024 * 1024
/** Wazzup: любое вложение ≤ 10 МБ. */
export const WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024

export type CrmIntegrationUploadedUserFile = {
  id: number
  path: string | null
  name: string | null
}

const getUploadErrorText = (fileName: string): string =>
  `Не удалось загрузить файл «${fileName}»`

const getFileExtension = (fileName: string): string => {
  const match = fileName.trim().match(/\.([^.]+)$/i)
  return match ? match[1].toLowerCase() : ''
}

export const isCrmIntegrationOutgoingImageFile = (file: File): boolean => {
  if (file.type.startsWith('image/')) {
    return true
  }

  return IMAGE_EXTENSION_SET.has(getFileExtension(file.name))
}

export const isCrmIntegrationOutgoingDocumentFile = (file: File): boolean => {
  if (isCrmIntegrationOutgoingVideoFile(file)) {
    return false
  }

  if (
    file.type.startsWith('application/') ||
    file.type === 'text/plain' ||
    file.type === 'text/csv' ||
    file.type === 'text/rtf'
  ) {
    return !file.type.startsWith('image/')
  }

  return DOCUMENT_EXTENSION_SET.has(getFileExtension(file.name))
}

export const isCrmIntegrationOutgoingVideoFile = (file: File): boolean => {
  if (file.type.startsWith('video/')) {
    return true
  }

  return VIDEO_EXTENSION_SET.has(getFileExtension(file.name))
}

export const isCrmIntegrationOutgoingAttachableFile = (file: File): boolean =>
  isCrmIntegrationOutgoingImageFile(file) ||
  isCrmIntegrationOutgoingDocumentFile(file) ||
  isCrmIntegrationOutgoingVideoFile(file)

const formatOutgoingLimitBytes = (sizeBytes: number): string => {
  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} КБ`
  }

  return `${Math.round(sizeBytes / (1024 * 1024))} МБ`
}

export const validateCrmIntegrationOutgoingVideoFile = (
  file: File,
  provider: CrmChatProviderKey,
): string | null => {
  if (!isCrmIntegrationOutgoingVideoFile(file)) {
    return null
  }

  const maxSizeByProvider: Record<CrmChatProviderKey, number> = {
    telegram: TELEGRAM_VIDEO_MAX_BYTES,
    vkontakte: VK_VIDEO_MAX_BYTES,
    max: MAX_VIDEO_MAX_BYTES,
    wazzup: WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
  }

  const maxSize = maxSizeByProvider[provider]
  if (file.size > maxSize) {
    return `Размер видео не должен превышать ${formatOutgoingLimitBytes(maxSize)}`
  }

  const extension = getFileExtension(file.name)
  const isMp4 =
    file.type === 'video/mp4' || extension === 'mp4' || file.type.includes('mp4')

  if (provider === 'telegram' && extension && !isMp4) {
    return 'Для Telegram отправляйте видео в формате MP4'
  }

  if (provider === 'max') {
    const allowedMaxExtensions = new Set(['mp4', 'mov', 'mkv', 'webm'])
    if (extension && !allowedMaxExtensions.has(extension)) {
      return 'Для MAX поддерживаются форматы MP4, MOV, MKV и WEBM'
    }
  }

  return null
}

/** Общий лимит размера для Wazzup (любой kind). Для остальных — null (свои проверки). */
export const validateCrmIntegrationOutgoingAttachmentSize = (
  file: File,
  provider: CrmChatProviderKey,
): string | null => {
  if (provider !== 'wazzup') {
    return null
  }

  if (file.size > WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES) {
    return `Размер вложения для Wazzup не должен превышать ${formatOutgoingLimitBytes(
      WAZZUP_OUTGOING_ATTACHMENT_MAX_BYTES,
    )}`
  }

  return null
}

export const inferCrmIntegrationOutgoingAttachmentKind = (
  file: File,
  options?: { forceVoice?: boolean },
): CrmIntegrationOutgoingAttachmentKind => {
  if (options?.forceVoice) {
    return 'voice'
  }

  if (isCrmIntegrationOutgoingImageFile(file)) {
    return 'image'
  }

  if (isCrmIntegrationOutgoingVideoFile(file)) {
    return 'video'
  }

  if (file.type.startsWith('video/')) {
    return 'video'
  }

  if (file.type.startsWith('audio/')) {
    return 'audio'
  }

  return 'file'
}

const parseUploadedUserFilePayload = (res: unknown): Record<string, unknown> | null => {
  if (!res || typeof res !== 'object') {
    return null
  }

  const record = res as { status?: unknown; data?: unknown }
  if (record.status === 'success' && record.data != null && typeof record.data === 'object') {
    return record.data as Record<string, unknown>
  }

  const axiosLike = res as { data?: { status?: unknown; data?: unknown } }
  if (
    axiosLike.data?.status === 'success' &&
    axiosLike.data.data != null &&
    typeof axiosLike.data.data === 'object'
  ) {
    return axiosLike.data.data as Record<string, unknown>
  }

  return null
}

const parseUserFileDetailPayload = (res: unknown): Record<string, unknown> | null => {
  const direct = parseUploadedUserFilePayload(res)
  if (direct != null) {
    return direct
  }

  if (!res || typeof res !== 'object') {
    return null
  }

  const axiosLike = res as { data?: unknown }
  if (axiosLike.data != null && typeof axiosLike.data === 'object') {
    return axiosLike.data as Record<string, unknown>
  }

  return null
}

const getMaxOutgoingPublicOrigin = (): string | null => {
  const origin = process.env.REACT_APP_OUTGOING_ATTACHMENT_PUBLIC_ORIGIN?.trim()
  return origin ? origin.replace(/\/$/, '') : null
}

const replaceLocalhostOrigin = (url: string): string => {
  const publicOrigin = getMaxOutgoingPublicOrigin()
  if (!publicOrigin) {
    return url
  }

  try {
    const parsed = new URL(url)
    if (parsed.hostname !== 'localhost' && parsed.hostname !== '127.0.0.1') {
      return url
    }

    return `${publicOrigin}${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return url
  }
}

/**
 * Публичный HTTPS URL для MAX send — MAX скачивает файл снаружи.
 * Локалка: REACT_APP_OUTGOING_ATTACHMENT_PUBLIC_ORIGIN = ngrok на API :8000.
 */
const resolveMaxOutgoingPublicUrl = (path: string | null | undefined): string | null => {
  if (!path?.trim()) {
    return null
  }

  const trimmed = path.trim()
  const publicOrigin = getMaxOutgoingPublicOrigin()

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return replaceLocalhostOrigin(trimmed)
  }

  if (!publicOrigin) {
    return null
  }

  const normalizedPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  return `${publicOrigin}${normalizedPath}`
}

const resolveUserFileContentType = (
  file: File,
  options?: { forceVoice?: boolean },
): string | null => {
  const kind = inferCrmIntegrationOutgoingAttachmentKind(file, options)

  if (kind === 'image') {
    return 'image'
  }

  if (kind === 'file') {
    return 'document'
  }

  return null
}

export const uploadCrmIntegrationUserFile = async (
  file: File,
  userId: number,
  options?: { displayName?: string; forceVoice?: boolean },
): Promise<CrmIntegrationUploadedUserFile> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('UserFilesForm[user_id]', String(userId))
  formData.append('UserFilesForm[name]', options?.displayName ?? file.name)

  const contentType = resolveUserFileContentType(file, options)
  if (contentType) {
    formData.append('content_type', contentType)
  }

  const res = await usersAPI.uploadPlatformMedia(formData)

  const data = parseUploadedUserFilePayload(res)
  if (!data) {
    throw new Error(getUploadErrorText(file.name))
  }

  const fileId = Number(data.id)

  if (!Number.isFinite(fileId) || fileId <= 0) {
    throw new Error(getUploadErrorText(file.name))
  }

  let path = typeof data?.path === 'string' ? data.path : null

  if (!path) {
    const detailRes = await usersAPI.getUserFile(fileId)
    const detail = parseUserFileDetailPayload(detailRes)
    path = typeof detail?.path === 'string' ? detail.path : null
  }

  return {
    id: fileId,
    path,
    name: typeof data.name === 'string' ? data.name : file.name,
  }
}

export const buildCrmIntegrationOutgoingAttachment = (
  file: File,
  uploaded: CrmIntegrationUploadedUserFile,
  provider: CrmChatProviderKey,
  options?: { forceVoice?: boolean; duration?: number | null },
): CrmIntegrationOutgoingAttachment => {
  const kind = inferCrmIntegrationOutgoingAttachmentKind(file, options)
  const attachment: CrmIntegrationOutgoingAttachment = {
    kind,
    name: file.name,
    mime: file.type || undefined,
    size: file.size,
  }

  if (
    kind === 'voice' &&
    options?.duration != null &&
    Number.isFinite(options.duration) &&
    options.duration > 0
  ) {
    attachment.duration = Math.round(options.duration)
  }

  if (provider === 'vkontakte' || provider === 'telegram' || provider === 'wazzup') {
    return {
      ...attachment,
      user_file_id: uploaded.id,
    }
  }

  const url = resolveMaxOutgoingPublicUrl(uploaded.path)
  if (!url) {
    throw new Error(
      'Для отправки вложений в MAX задайте REACT_APP_OUTGOING_ATTACHMENT_PUBLIC_ORIGIN (ngrok на API :8000)',
    )
  }

  return {
    ...attachment,
    url,
  }
}

export const uploadCrmIntegrationOutgoingAttachment = async (
  file: File,
  userId: number,
  provider: CrmChatProviderKey,
  options?: {
    displayName?: string
    forceVoice?: boolean
    duration?: number | null
  },
): Promise<CrmIntegrationOutgoingAttachment> => {
  const uploaded = await uploadCrmIntegrationUserFile(file, userId, options)
  return buildCrmIntegrationOutgoingAttachment(file, uploaded, provider, options)
}
